// src/pages/admin/manager/evaluaciones/UsuarioEvaluacionesPage.tsx
import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  getHistorialEvaluacionesUsuario,
  asignarEvaluacionIndividual,
  type HistorialEvaluacion,
} from '../../../../api/formularios';
import AsignarEvaluacionDialog from '../../../../components/formularios/AsignarEvaluacionDialog';
import { useUsuarios } from '../../../../hooks/useUsuarios';

export default function UsuarioEvaluacionesPage() {
  const { usuarioId } = useParams<{ usuarioId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();

  const [historial, setHistorial] = useState<HistorialEvaluacion[]>([]);
  const [loading, setLoading] = useState(false);
  const [asignarOpen, setAsignarOpen] = useState(false);
  const { rows: usuarios = [] } = useUsuarios({ soloActivos: false });

  const usuario = usuarios.find((u) => u.id === Number(usuarioId));

  const loadData = useCallback(async () => {
    if (!usuarioId) return;
    setLoading(true);
    try {
      const historialData = await getHistorialEvaluacionesUsuario(Number(usuarioId));
      setHistorial(historialData || []);
    } catch (error: any) {
      enqueueSnackbar('Error al cargar datos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [usuarioId, enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleReenviar = async (evaluacionId: number) => {
    if (!usuarioId) return;
    try {
      await asignarEvaluacionIndividual(Number(evaluacionId), {
        usuarioId: Number(usuarioId),
        esRecurrente: false,
      });
      enqueueSnackbar('Evaluación reenviada', { variant: 'success' });
      loadData();
    } catch (error: any) {
      enqueueSnackbar('Error al reenviar evaluación', { variant: 'error' });
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'success';
      case 'pendiente':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton onClick={() => navigate('/admin/manager/usuarios')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Evaluaciones de {usuario?.nombreCompleto || 'Usuario'}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AssignmentIndIcon />}
          onClick={() => setAsignarOpen(true)}
        >
          Asignar Evaluación
        </Button>
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {historial.length === 0 ? (
            <Paper sx={{ p: 3 }}>
              <Typography align="center" color="text.secondary">
                No hay evaluaciones asignadas a este usuario
              </Typography>
            </Paper>
          ) : (
            historial.map((evalItem) => (
              <Accordion key={evalItem.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {evalItem.titulo}
                    </Typography>
                    <Chip
                      label={evalItem.estado}
                      size="small"
                      color={getEstadoColor(evalItem.estado) as any}
                    />
                    {evalItem.ultimaCalificacion !== undefined && (
                      <Chip
                        label={`${evalItem.ultimaCalificacion} pts`}
                        size="small"
                        color={evalItem.ultimoAprobado ? 'success' : 'error'}
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {evalItem.descripcion && (
                      <Typography variant="body2" color="text.secondary">
                        {evalItem.descripcion}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Asignado:</strong>{' '}
                      {new Date(evalItem.asignadoAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Intentos:</strong> {evalItem.intentos}
                    </Typography>
                    {evalItem.completados.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Historial de Intentos:
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Intento</TableCell>
                              <TableCell>Fecha</TableCell>
                              <TableCell>Calificación</TableCell>
                              <TableCell>Estado</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {evalItem.completados.map((completado, idx) => (
                              <TableRow key={idx}>
                                <TableCell>#{completado.intentoNumero}</TableCell>
                                <TableCell>
                                  {new Date(completado.completadoAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  {completado.calificacion !== undefined
                                    ? `${completado.calificacion} pts`
                                    : '-'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={completado.aprobado ? 'Aprobado' : 'Reprobado'}
                                    size="small"
                                    color={completado.aprobado ? 'success' : 'error'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                    {(evalItem.estado === 'pendiente' ||
                      (evalItem.completados &&
                        evalItem.completados.length > 0 &&
                        !evalItem.completados[evalItem.completados.length - 1].aprobado)) && (
                      <Button variant="outlined" onClick={() => handleReenviar(evalItem.id)}>
                        Reenviar Evaluación
                      </Button>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </>
      )}

      <AsignarEvaluacionDialog
        open={asignarOpen}
        usuarioId={usuarioId ? Number(usuarioId) : undefined}
        onClose={() => setAsignarOpen(false)}
        onSaved={loadData}
      />
    </Stack>
  );
}
